import { Button, Icon, Dialog, Flex, IconButton, Card, Text, Callout } from '@scale/scaleui-radix';
import { faClose, faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { UserAutocomplete } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/UserAutocomplete';
import { useState } from 'react';
import { UserIdOrEmailInput } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/UserIdOrEmailInput';
import { useMutation } from '@tanstack/react-query-v4';
import { editSession, collectAttendance } from '../api';
import {
  CollectionSessionWithHydratedUsers,
  HydratedUser,
} from '@scale/shared/webinarSessions/types';
import { toast } from 'react-toastify';
import WebinarError from 'react-scale/components/WebinarSessions/components/WebinarError';
import { uniq } from 'lodash';
import { UserTable } from 'react-scale/corp/views/CorpProjectGroupManager/apps/Webinars/components/UserTable';
import moment from 'moment-timezone';

const AddAttendees = ({
  session,
  refetch,
}: {
  session: CollectionSessionWithHydratedUsers;
  refetch: () => void;
}) => {
  const attendeesById = new Map(session.attendees.map(attendee => [attendee._id, attendee]));
  const [newIds, setNewIds] = useState<string[]>([]);
  const [existingIds, setExistingIds] = useState<string[]>([]);
  const [open, setOpen] = useState(false);

  const setUserIds = (userIds: string[]) => {
    setExistingIds(userIds.filter(id => attendeesById.has(id)));
    setNewIds(userIds.filter(id => !attendeesById.has(id)));
  };

  const existingAttendees = existingIds
    .map(id => attendeesById.get(id))
    .filter((x): x is HydratedUser => x !== undefined);

  const {
    mutate: addAttendees,
    isLoading,
    error,
  } = useMutation({
    mutationFn: () => {
      return editSession({
        sessionId: session._id,
        changedFields: {
          attendees: uniq([...Array.from(attendeesById.keys()), ...newIds]),
        },
        applyToFutureSessions: false,
      });
    },
    onSuccess: () => {
      toast.success('Attendees added successfully');
      setNewIds([]);
      setExistingIds([]);
      refetch();
      setOpen(false);
    },
  });

  const { mutate: runAttendanceSync, isLoading: isRunningAttendanceSync } = useMutation({
    mutationFn: () => collectAttendance(session._id),
    onSuccess: () => {
      toast.success('Attendance collected successfully!');
      refetch();
    },
  });

  if (error) {
    return <WebinarError error={error} />;
  }

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger>
        <Flex direction="column" gap="1" align="end">
          <Button variant="ghost" color="accent" onClick={() => setOpen(true)} className="w-fit">
            Add attendees
          </Button>
          <Button
            variant="ghost"
            color="accent"
            onClick={() => runAttendanceSync()}
            loading={isRunningAttendanceSync}
            className="w-fit"
          >
            Run attendance sync
          </Button>
          {session.attendanceSyncedAt && (
            <Text size="2" color="neutral">
              (Last synced {moment(session.attendanceSyncedAt).format('MMM D, YYYY h:mm A')})
            </Text>
          )}
        </Flex>
      </Dialog.Trigger>
      <Dialog.Content className="scale-tailwind">
        <Flex direction="column" gap="4">
          <Flex justify="between">
            <Dialog.Title>Add attendees to this session</Dialog.Title>
            <Dialog.Close>
              <IconButton color="neutral" variant="ghost">
                <Icon icon={faClose} label="Close" size="1" />
              </IconButton>
            </Dialog.Close>
          </Flex>
          <Card variant="surface" className="flex flex-col gap-4">
            <Flex direction="column" gap="2">
              <Text as="label" size="2" weight="medium">
                Search for users
              </Text>
              <UserAutocomplete
                userIds={newIds}
                onUserIdsChange={setUserIds}
                showValues={false}
                limitToScaliens={false}
                limitToContributors={true}
              />
            </Flex>
            <Flex direction="column" gap="2">
              <Text as="label" size="2" weight="medium">
                Paste in user IDs or emails
              </Text>
              <UserIdOrEmailInput userIds={newIds} setUsersIds={setUserIds} />
            </Flex>
            {existingAttendees.length > 0 && (
              <Callout.Root size="1" color="neutral">
                <Callout.Icon>
                  <Icon icon={faCircleInfo} label="info" size="1" />
                </Callout.Icon>
                <Callout.Text>
                  {existingAttendees.length} attendee(s) were ignored because they already exist in
                  the attendees list.
                </Callout.Text>
              </Callout.Root>
            )}
            {newIds.length > 0 && (
              <UserTable
                userIds={newIds}
                invitedUserIds={newIds}
                setInvitedUserIds={setUserIds}
                label="New attendees"
              />
            )}
          </Card>
          <Flex justify="end">
            <Button
              variant="solid"
              color="accent"
              disabled={newIds.length === 0}
              loading={isLoading}
              onClick={() => addAttendees()}
            >
              Save
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default AddAttendees;
